import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Segment, TabPanel, Tabs } from 'tea-component';
import AccountList from './AccountList';
import FilterList from './FilterList';
import SaveFilterModal from './SaveFilterModal';
import { gameInfoType } from '@op/types/common';
import { ClassSpecial } from '@op/constants/common';
import { AccountOption, getAccountListValue } from '../../utils';
import useLanguage from '@op/hooks/useLanguage';
import SvgIcon from '@home/components/SvgIcon';
import './index.scss';
import { useStateRef } from '@op/hooks/common';

interface AccountSelectProps {
  options: AccountOption[];
  onChange?: (value: string[]) => void;
  onClose?: (value: string[]) => void;
  gameInfo: gameInfoType;
  defaultValue: string[];
}

const tabs = [
  { text: 'All', value: 'all' },
  { text: 'Filter Setting', value: 'filter' },
];

export default function AccountSelect({
  options,
  onChange,
  gameInfo,
  defaultValue,
  onClose,
}: AccountSelectProps) {
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const [select, setSelect] = useState('all');
  const [selectFilterRef, selectFilter, setSelectFilter] = useStateRef<{
    text?: string;
    value?: number;
  }>({});
  const [selectedAccountsRef, selectedAccounts, setSelectedAccounts] =
    useStateRef<string[]>(defaultValue);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setSelectedAccounts(defaultValue)
  }, [defaultValue])

  function close() {
    setVisible(false);
  }

  const allAccountsList = useMemo(() => {
    return getAccountListValue(options);
  }, [options]);

  // 处理选中账号变化
  const handleSelectionChange = (
    accounts: string[],
    isFilterChange = false,
  ) => {
    setSelectedAccounts(accounts);
    if (!isFilterChange) {
      setSelectFilter({});
    } else {
      onClose?.(selectedAccounts!);
    }
    onChange?.(accounts);
  };

  function renderButtonElement() {
    let renderText = selectFilter?.text;

    if (!renderText) {
      if (allAccountsList.length) {
        renderText = `${selectedAccounts?.length}`;
      }

      if (selectedAccounts?.length === 0) {
        renderText = formatMessage({ id: 'None' });
      }

      if (allAccountsList.length === selectedAccounts?.length) {
        renderText = formatMessage({ id: 'All' });
      }
    }

    return (
      <div className="dbo-button-box">
        <div className="dbo-placeholder-title">{formatMessage('Account')}</div>
        <div className="dbo-button-value">{renderText}</div>
      </div>
    );
  }

  return (
    <>
      <Dropdown
        trigger="click"
        className="dbo-account-select-wrapper"
        boxClassName="dbo-account-select-dropdown-box"
        clickClose={false}
        style={{ marginRight: 10 }}
        button={renderButtonElement()}
        size="m"
        boxStyle={{ width: 292 }}
        appearance="button"
        footer={
          <Button type="link" onClick={() => setVisible(true)}>
            {/* <Icon type="plus" /> */}
            <SvgIcon type="icon_add_game" className="dbo-add-game-icon" />
            {formatMessage('Save Filter Setting')}
          </Button>
        }
        onClose={() => { 
          setSelect(selectFilterRef.current?.text  ? "filter" : 'all');
          onClose?.(selectedAccountsRef.current!);
        }}
      >
        {(close) => (
          <>
            <Segment
              value={select}
              onChange={(value) => {
                setSelect(value);
              }}
              options={formatOptionsMessage(tabs)}
            />

            <Tabs
              tabs={tabs.map((item) => ({
                label: item.text,
                id: item.value,
              }))}
              activeId={select}
              className={ClassSpecial.TAB_NONE}
            >
              <TabPanel id={'all'}>
                <AccountList
                  options={options}
                  onChange={handleSelectionChange}
                  defaultValue={selectedAccounts}
                ></AccountList>
              </TabPanel>
              <TabPanel id={'filter'}>
                <FilterList
                  selectFilter={selectFilter!}
                  setSelectFilter={setSelectFilter}
                  gameInfo={gameInfo}
                  handleSelectionChange={handleSelectionChange}
                  options={options}
                  close={() => close()}
                  selectedAccounts={selectedAccounts!}
                ></FilterList>
              </TabPanel>
            </Tabs>
          </>
        )}
      </Dropdown>

      {visible && (
        <SaveFilterModal
          close={() => close()}
          visible={visible}
          gameInfo={gameInfo}
          defaultSelected={selectedAccounts!}
          options={options}
        ></SaveFilterModal>
      )}
    </>
  );
}
